import { useEffect, useState } from 'react'
import { apiGet, apiSend } from '../api/client'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner, Field, Notice, TextInput } from '../ui/controls'
import type { MessageKey } from '../i18n/messages'

type ContactMethodType = 'Mobile' | 'WeChat' | 'WhatsApp' | 'Email' | 'Line' | 'Other'

interface ContactMethod {
  methodType: ContactMethodType
  value: string
}

const TYPES: readonly ContactMethodType[] = ['Mobile', 'WeChat', 'WhatsApp', 'Email', 'Line', 'Other']

/**
 * The caller's own contact details.
 *
 * **This is the thing §2.1 says no payment can buy.** It is stored here and released only when a
 * connection forms, to both people at the same moment — which is exactly what the notice on this
 * page says, because someone typing their phone number into a matchmaking site deserves to read
 * the rule at the moment they are deciding whether to trust it.
 *
 * At least one entry is required before a profile can complete (`ProfileCompletionService`).
 */
export function ContactMethodsPage() {
  const { t } = useI18n()

  const [items, setItems] = useState<ContactMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    apiGet<{ items: ContactMethod[] }>('/api/profile/me/contact-methods')
      .then((body) => setItems(body.items.length > 0 ? body.items : [{ methodType: 'Mobile', value: '' }]))
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return <Busy />
  }

  const update = (index: number, patch: Partial<ContactMethod>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...patch } : item)))
    setSaved(false)
  }

  return (
    <form
      className="page stack"
      onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        setBusy(true)

        // Empty rows are a side effect of the "add one" button, not something the user asked to
        // save. Dropping them here keeps an accidental blank from being rejected by the server.
        const payload = items.filter((item) => item.value.trim().length > 0)

        apiSend<void>('PUT', '/api/profile/me/contact-methods', { items: payload })
          .then(() => setSaved(true))
          .catch(setError)
          .finally(() => setBusy(false))
      }}
    >
      <h1>{t('contact.title')}</h1>

      <p className="lede">{t('contact.required')}</p>
      <Notice>{t('contact.redline')}</Notice>

      <ErrorBanner error={error} />
      {saved ? <Notice>{t('common.saved')}</Notice> : null}

      <section className="card stack">
        {items.map((item, index) => (
          <div className="contact-row" key={index}>
            <Field label={t('contact.type')}>
              <select
                className="control"
                value={item.methodType}
                onChange={(event) =>
                  update(index, { methodType: event.target.value as ContactMethodType })
                }
              >
                {TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`contact.type.${type}` as MessageKey)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('contact.value')}>
              <TextInput
                value={item.value}
                onChange={(value) => update(index, { value })}
                maxLength={200}
              />
            </Field>

            {items.length > 1 ? (
              <button
                type="button"
                className="link-button"
                onClick={() => setItems(items.filter((_, i) => i !== index))}
              >
                {t('contact.remove')}
              </button>
            ) : null}
          </div>
        ))}

        {items.length < 5 ? (
          <button
            type="button"
            className="link-button"
            onClick={() => setItems([...items, { methodType: 'WeChat', value: '' }])}
          >
            {t('contact.add')}
          </button>
        ) : null}
      </section>

      <button type="submit" className="primary" disabled={busy}>
        {busy ? t('common.saving') : t('common.save')}
      </button>
    </form>
  )
}
